import React, { createContext, useContext, useState, useEffect } from 'react';

export type HolidayTheme = 
  | 'default'
  | 'chinese-new-year'
  | 'songkran'
  | 'halloween'
  | 'valentine';

interface HolidayThemeConfig {
  id: HolidayTheme;
  name: string;
  nameEn: string;
  icon: string;
  colors: {
    primary: string;
    primaryForeground: string;
    secondary: string;
    accent: string;
    background: string;
    foreground: string;
    muted: string;
    mutedForeground: string;
    card: string;
    border: string;
  };
  gradients: {
    primary: string;
    secondary: string;
    accent: string;
  };
  effects?: {
    particles?: string;
    animation?: string;
  };
}

const HOLIDAY_THEMES: Record<HolidayTheme, HolidayThemeConfig> = {
  default: {
    id: 'default',
    name: 'ธีมปกติ',
    nameEn: 'Default',
    icon: '⚡',
    colors: {
      primary: '222.2 84% 4.9%',
      primaryForeground: '210 40% 98%',
      secondary: '210 40% 96%',
      accent: '210 40% 96%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      muted: '210 40% 96%',
      mutedForeground: '215.4 16.3% 46.9%',
      card: '0 0% 100%',
      border: '214.3 31.8% 91.4%',
    },
    gradients: {
      primary: 'linear-gradient(135deg, hsl(222.2 84% 4.9%), hsl(217.2 91.2% 59.8%))',
      secondary: 'linear-gradient(135deg, hsl(210 40% 96%), hsl(214.3 31.8% 91.4%))',
      accent: 'linear-gradient(135deg, hsl(210 40% 96%), hsl(215.4 16.3% 46.9%))',
    },
  },
  'chinese-new-year': {
    id: 'chinese-new-year',
    name: 'ตรุษจีน',
    nameEn: 'Chinese New Year',
    icon: '🏮',
    colors: {
      primary: '0 84% 60%', // แดงเฮง
      primaryForeground: '47 96% 89%', // ทองอ่อน
      secondary: '45 93% 47%', // ทอง
      accent: '355 70% 54%', // แดงเข้ม
      background: '47 100% 96%', // ครีมอ่อน
      foreground: '0 84% 20%', // แดงเข้มมาก
      muted: '45 100% 92%', // ทองอ่อนมาก
      mutedForeground: '0 84% 35%',
      card: '0 0% 100%',
      border: '45 93% 80%',
    },
    gradients: {
      primary: 'linear-gradient(135deg, hsl(0 84% 60%), hsl(355 70% 54%))',
      secondary: 'linear-gradient(135deg, hsl(45 93% 47%), hsl(47 96% 89%))',
      accent: 'linear-gradient(135deg, hsl(355 70% 54%), hsl(0 84% 60%))',
    },
    effects: {
      particles: 'gold-sparkles',
      animation: 'dragon-dance',
    },
  },
  songkran: {
    id: 'songkran',
    name: 'สงกรานต์',
    nameEn: 'Songkran',
    icon: '💧',
    colors: {
      primary: '199 89% 48%', // น้ำเงินใส
      primaryForeground: '0 0% 100%',
      secondary: '164 86% 49%', // เขียวมิ้นต์
      accent: '185 96% 53%', // ฟ้าใส
      background: '195 100% 98%', // ฟ้าอ่อนมาก
      foreground: '199 89% 20%',
      muted: '195 100% 95%',
      mutedForeground: '199 89% 35%',
      card: '0 0% 100%',
      border: '195 100% 90%',
    },
    gradients: {
      primary: 'linear-gradient(135deg, hsl(199 89% 48%), hsl(185 96% 53%))',
      secondary: 'linear-gradient(135deg, hsl(164 86% 49%), hsl(199 89% 48%))',
      accent: 'linear-gradient(135deg, hsl(185 96% 53%), hsl(164 86% 49%))',
    },
    effects: {
      particles: 'water-drops',
      animation: 'water-splash',
    },
  },
  halloween: {
    id: 'halloween',
    name: 'ฮาโลวีน',
    nameEn: 'Halloween',
    icon: '🎃',
    colors: {
      primary: '25 95% 53%', // ส้มฟักทอง
      primaryForeground: '0 0% 100%',
      secondary: '271 100% 12%', // ม่วงเข้ม
      accent: '38 92% 50%', // ส้มเข้ม
      background: '270 100% 5%', // ดำม่วง
      foreground: '47 96% 89%', // ครีม
      muted: '271 100% 15%',
      mutedForeground: '47 96% 70%',
      card: '271 100% 8%',
      border: '271 100% 20%',
    },
    gradients: {
      primary: 'linear-gradient(135deg, hsl(25 95% 53%), hsl(38 92% 50%))',
      secondary: 'linear-gradient(135deg, hsl(271 100% 12%), hsl(270 100% 5%))',
      accent: 'linear-gradient(135deg, hsl(38 92% 50%), hsl(25 95% 53%))',
    },
    effects: {
      particles: 'bats-flying',
      animation: 'spooky-glow',
    },
  },
  valentine: {
    id: 'valentine',
    name: 'วันแห่งความรัก',
    nameEn: 'Valentine',
    icon: '💕',
    colors: {
      primary: '330 81% 60%', // ชมพูเข้ม
      primaryForeground: '0 0% 100%',
      secondary: '351 83% 61%', // แดงอ่อน
      accent: '320 65% 52%', // ชมพูม่วง
      background: '327 100% 98%', // ชมพูอ่อนมาก
      foreground: '330 81% 25%',
      muted: '327 100% 95%',
      mutedForeground: '330 81% 40%',
      card: '0 0% 100%',
      border: '327 100% 90%',
    },
    gradients: {
      primary: 'linear-gradient(135deg, hsl(330 81% 60%), hsl(351 83% 61%))',
      secondary: 'linear-gradient(135deg, hsl(351 83% 61%), hsl(320 65% 52%))',
      accent: 'linear-gradient(135deg, hsl(320 65% 52%), hsl(330 81% 60%))',
    },
    effects: {
      particles: 'heart-sparkles',
      animation: 'romantic-glow',
    },
  },
};

interface HolidayThemeContextType {
  currentTheme: HolidayTheme;
  setTheme: (theme: HolidayTheme) => void;
  getThemeConfig: (theme: HolidayTheme) => HolidayThemeConfig;
  getAllThemes: () => HolidayThemeConfig[];
  isLoading: boolean;
}

const HolidayThemeContext = createContext<HolidayThemeContextType | null>(null);

export function HolidayThemeProvider({ children }: { children: React.ReactNode }) {
  const [currentTheme, setCurrentTheme] = useState<HolidayTheme>('default');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Load theme from localStorage for now
    const savedTheme = localStorage.getItem('holiday-theme') as HolidayTheme;
    if (savedTheme && HOLIDAY_THEMES[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);

  useEffect(() => {
    applyThemeToDocument(currentTheme);
  }, [currentTheme]);

  const setTheme = (theme: HolidayTheme) => {
    setCurrentTheme(theme);
    localStorage.setItem('holiday-theme', theme);
  };

  const applyThemeToDocument = (theme: HolidayTheme) => {
    const config = HOLIDAY_THEMES[theme];
    const root = document.documentElement;

    // Apply CSS variables
    Object.entries(config.colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`, value);
    });

    // Apply gradients
    Object.entries(config.gradients).forEach(([key, value]) => {
      root.style.setProperty(`--gradient-${key}`, value);
    });

    // Apply theme class
    root.className = root.className.replace(/theme-\w+/g, '');
    root.classList.add(`theme-${theme}`);
  };

  const getThemeConfig = (theme: HolidayTheme) => HOLIDAY_THEMES[theme];
  const getAllThemes = () => Object.values(HOLIDAY_THEMES);

  return (
    <HolidayThemeContext.Provider
      value={{
        currentTheme,
        setTheme,
        getThemeConfig,
        getAllThemes,
        isLoading,
      }}
    >
      {children}
    </HolidayThemeContext.Provider>
  );
}

export function useHolidayTheme() {
  const context = useContext(HolidayThemeContext);
  if (!context) {
    throw new Error('useHolidayTheme must be used within a HolidayThemeProvider');
  }
  return context;
}